/* Nav icons, carried over from the prototype so the menu reads the same.
 *
 * Inline rather than an icon package: fourteen 24x24 outlines are a few hundred
 * bytes of JSX, against a dependency that ships thousands and needs tree-shaking
 * to be checked on every build. They inherit currentColor, so the active state
 * recolours them with no extra rule. */
import type { ReactNode } from 'react';

const S = ({ children }: { children: ReactNode }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const ICONS: Record<string, ReactNode> = {
  dashboard: (
    <S>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <rect x="13" y="3" width="8" height="5" rx="1.5" />
      <rect x="13" y="11" width="8" height="10" rx="1.5" />
      <rect x="3" y="14" width="8" height="7" rx="1.5" />
    </S>
  ),
  stations: <S><path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" /></S>,
  chargepoints: (
    <S>
      <rect x="3" y="7" width="11" height="12" rx="2" />
      <path d="M6.5 11h4" />
      <path d="M17 10v6a2.5 2.5 0 0 0 4 0v-5l-2.5-3" />
    </S>
  ),
  /* A play triangle inside a bolt's territory: this one does something to the
     hardware rather than describing it. */
  testbench: (
    <S>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 8.5l6 3.5-6 3.5z" />
    </S>
  ),
  /* A plug on a lead: one bay, not the cabinet it lives in. */
  connectors: (
    <S>
      <path d="M9 3v5" />
      <path d="M15 3v5" />
      <rect x="6" y="8" width="12" height="6" rx="2" />
      <path d="M12 14v3a4 4 0 0 1-4 4H6" />
    </S>
  ),
  ocpp: (
    <S>
      <path d="M8.5 8.5L5 12l3.5 3.5" />
      <path d="M15.5 8.5L19 12l-3.5 3.5" />
      <path d="M13.2 5.5l-2.4 13" />
    </S>
  ),
  drivers: (
    <S>
      <circle cx="12" cy="8.5" r="3.7" />
      <path d="M4.5 20c1.3-4 4.2-6 7.5-6s6.2 2 7.5 6" />
    </S>
  ),
  agents: (
    <S>
      <circle cx="9" cy="9" r="3" />
      <path d="M2 20c1-3.5 3.6-5 7-5s6 1.5 7 5" />
      <circle cx="17.5" cy="8" r="2.4" />
    </S>
  ),
  transactions: <S><path d="M4 6h16M4 12h16M4 18h10" /></S>,
  tariffs: (
    <S>
      <path d="M20 12l-8 8-8-8 8-8h8z" />
      <circle cx="16" cy="8" r="1.3" fill="currentColor" />
    </S>
  ),
  disputes: <S><path d="M5 21V4M5 4h11l-2 4 2 4H5" /></S>,
  reports: <S><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></S>,
  users: (
    <S>
      <circle cx="9" cy="8" r="3" />
      <path d="M2.5 20c1-3.2 3.4-4.6 6.5-4.6 1.4 0 2.7 .3 3.8 .9" />
      <circle cx="17.5" cy="10.5" r="2.5" />
      <path d="M13.5 20c.6-2 2.1-3 4-3s3.4 1 4 3" />
    </S>
  ),
  releases: (
    <S>
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
      <path d="M12 6.5v6M9.4 10l2.6 2.6L14.6 10" />
    </S>
  ),
  maps: (
    <S>
      <path d="M9 3.5L3.5 6v14L9 17.5l6 3 5.5-2.5v-14L15 6.5z" />
      <path d="M9 3.5v14M15 6.5v14" />
    </S>
  ),
  activity: (
    <S>
      <path d="M12 7v5l3 2" />
      <circle cx="12" cy="12" r="8.5" />
    </S>
  ),
};
